<template>
  <div v-if="true">
    <v-card v-if="loaded" class="mt-3" color="blue-grey lighten-5" elevation="0">
      <v-card-text>
        <v-row>
          <v-col lg="3" md="6" cols="12">
            <v-card elevation="1" height="270">
              <v-card-title class="text-h5">
                {{ $t('performance_dashboard_ui.in_spec_routes') }}
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.temperature_violations.main.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.temperature_violations.main.text')"/>
                </info-modal>
              </v-card-title>
              <v-card-subtitle>
                {{ $t('performance_dashboard_ui.past_30_days' )}}
              </v-card-subtitle>
              <v-divider></v-divider>
              <v-card-text>
                <div class="text-subtitle-1" v-if="overallStatus.num_total_routes === 0">
                  {{ $t('no_data_available') }}
                </div>
                <v-list v-else class="py-0">
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-truck-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('in-spec') }}
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip color="success" dark>
                        {{  overallStatus.num_in_spec_routes.toLocaleString() }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon>mdi-alert</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      {{ $t('violating') }}
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip color="danger" dark>
                        {{ overallStatus.num_violating_routes.toLocaleString() }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col lg="3" md="6" cols="12">
            <temperature-violations-year-over-year
              v-if="loaded"
              :requested-customer-ids="requestedCustomerIds"
              :height="125"
              ref="violations-yoy-chart"/>
          </v-col>
          <v-col>
            <v-card elevation="1" height="270">
              <v-card-title class="text-h5 d-flex justify-space-between">
                <div class="d-flex align-center">
                <span>
                  {{ $t('performance_dashboard_ui.daily_trend') }}
                  <span class="subheader">{{ $t('performance_dashboard_ui.trailing_six_months') }}</span>
                </span>
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.fleet_health.six_month_trend.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.fleet_health.six_month_trend.text')"/>
                </info-modal>
                </div>
                <v-btn color="primary" @click="generateReport">
                  <v-icon>mdi-chart-areaspline</v-icon>
                  {{ $t('performance_dashboard_ui.see_full_report') }}
                </v-btn>
              </v-card-title>
              <v-card-text>
                <temperature-violations-chart ref="trailing-health-chart" :selected-customers="selectedCustomers" :height="170"/>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-card v-if="filteredCustomers.length > 1" class="mt-4" elevation="1">
          <v-card-title class="text-h5">
            <span>
              {{ $t('performance_dashboard_ui.dc_in_spec_routes') }}
            </span>
            <info-modal width="800">
              <v-card-title class="text-h5">
                {{ $t('performance_dashboard_ui.modal_info.temperature_violations.distribution_centers.title') }}
              </v-card-title>
              <v-card-text v-html="$t('performance_dashboard_ui.modal_info.temperature_violations.distribution_centers.text')"/>
            </info-modal>
          </v-card-title>
          <v-card-subtitle class="pb-0">
            {{ $t('performance_dashboard_ui.past_30_days' )}}
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-data-table
            class="vf-table"
            :items="filteredCustomers"
            :headers="tableFields"
            sort-by="percent_in_spec">
            <template #item.name="row">
              <router-link :to="`/performance/dashboard/customers/${row.item.id}`">{{ row.item.name }}</router-link>
            </template>
            <template #item.percent_in_spec="row">
              {{ round(row.item.percent_in_spec, 1) }}%
            </template>
            <template #item.num_in_spec="row">
              {{ row.item }}
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>
    <v-card v-else class="mt-3" color="blue-grey lighten-5" elevation="0">
      <v-card-text>
        <v-row>
          <v-col lg="3" md="6" cols="12">
            <v-skeleton-loader type="card" />
          </v-col>
          <v-col lg="3" md="6" cols="12">
            <v-skeleton-loader type="card" />
          </v-col>
          <v-col>
            <v-skeleton-loader type="card" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-skeleton-loader type="table"/>
    </v-card>
  </div>
</template>

<script>
import TemperatureViolationsChart from '@/components/performance-dashboard/TemperatureViolationsChart'
import TemperatureViolationsYearOverYear from '@/components/performance-dashboard/TemperatureViolationsYearOverYear'
import InfoModal from '@/components/shared/InfoModal'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import PerformanceDashboardComponentMixin from '@/components/performance-dashboard/PerformanceDashboardComponentMixin'
import MathUtil from '@/util/MathUtil'
import Moment from 'moment-timezone'
import { filter, map, round, invoke, each } from 'lodash'

export default {
  name: 'TemperatureViolationsDashboard',
  components: {
    TemperatureViolationsChart,
    TemperatureViolationsYearOverYear,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    PerformanceDashboardComponentMixin
  ],
  data () {
    return {
      // Whether the component has finished loading yet.
      loaded: false,
      temperatureViolationsData: null
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    round: round,
    /**
     * Load the data from the API.
     */
    async loadData () {
      this.loaded = false
      this.$emit('loading')

      const requestedCustomerIds = this.requestedCustomerIds.join(',')

      try {
        // Retrieve the temperature violations data
        const resp = await this.$httpAdapter.get('/customer-insights/temperature-violation-stats', {
          params: {
            customer_id: requestedCustomerIds
          }
        })
        this.temperatureViolationsData = resp.data
        this.$nextTick(() => {
          this.$emit('loaded', {
            value: this.overallStatus.percent_in_spec
          })
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loaded = true
      }
    },
    /**
     * Given a violation percentage, return the defined associated health status
     */
    getHealthStatus (value) {
      if (value > 90) {
        return 'healthy'
      }
      if (value >= 70 && value <= 90) {
        return 'unhealthy'
      }
      return 'danger'
    },
    /**
     * Generate the full Trailing 6-month Violations report and redirect to it.
     */
    async generateReport () {
      const reportParameters = {}

      reportParameters.startDate = Moment.tz(this.customerTimezone).subtract(6, 'months').format('YYYY-MM-DD')
      reportParameters.endDate = Moment.tz(this.customerTimezone).format('YYYY-MM-DD')
      reportParameters.customerIds = map(this.selectedCustomers, x => x.id)

      const report = this.$orm('Report').createRecord({
        type: 'TemperatureViolationHistoryReport',
        name: this.$t('performance_dashboard_ui.temperature_violation_history_report'),
        parameters: reportParameters
      })

      const savedReport = await this.$orm('Report').createOrUpdate(report)
      await this.$router.push(`/report/${savedReport.id}`)
    }
  },
  computed: {
    tableFields () {
      return [
        {
          value: 'name',
          text: this.$tc('name', 2)
        },
        {
          value: 'percent_in_spec',
          text: this.$t('performance_dashboard_ui.percent_in_spec')
        },
        {
          value: 'num_in_spec_routes',
          text: this.$t('performance_dashboard_ui.in_spec_routes')
        },
        {
          value: 'num_total_routes',
          text: this.$t('total_routes')
        }
      ]
    },
    /**
     * Compute the data for the overall status (used on the left-hand side)
     */
    overallStatus () {
      if (this.loaded && this.temperatureViolationsData != null) {
        const results = {
          num_total_routes: 0,
          num_violating_routes: 0,
          num_in_spec_routes: 0,
          percent_in_spec: 0
        }
        each(this.temperatureViolationsData, customer => {
          results.num_total_routes += customer.num_total_routes
          results.num_violating_routes += customer.num_violating_routes
          results.num_in_spec_routes += customer.num_in_spec_routes
        })
        results.percent_in_spec = results.num_total_routes === 0 ? 100 : MathUtil.percentage(results.num_in_spec_routes, results.num_total_routes, null)
        return results
      }
      return null
    },
    /**
     * Compile a list of customers sorted by their temperature violations percentage (how many routes had violations vs
     * the total number of assigned routes run.)
     */
    filteredCustomers () {
      const mapFn = (customer, customerId) => {
        customer.id = parseInt(customerId)
        customer.name = this.getCustomerName(customerId)
        // The API's percent_in_spec is pre-rounded to 2dp; recompute it from the counts so the table rounds only once
        customer.percent_in_spec = MathUtil.percentage(customer.num_in_spec_routes, customer.num_total_routes, null)
        return customer
      }
      // Filter out all customers with no data in the last 30 days
      const filterFn = (customer) => {
        return customer.num_total_routes > 0
      }
      return filter(map(this.temperatureViolationsData, mapFn), filterFn)
    }
  },
  watch: {
    selectedCustomers () {
      this.loadData()
    },
    // When tab becomes active, force the charts to redraw (in case the window size changed while on another tab)
    active (newValue, oldValue) {
      if (newValue === true) {
        invoke(this.$refs, 'violations-yoy-chart.draw')
        invoke(this.$refs, 'trailing-health-chart.fit')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.subheader {
  font-weight: normal;
  font-size: 18px;
  opacity: .8;
}
</style>
